import axios from 'axios';
import { AppDispatch } from '~/store/store';
import { refreshTokenThunk } from '~/store/users/thunks';

const api = axios.create({
  baseURL: import.meta.env.VITE_AXIOS_IDENTIFICATION_BASE_URL,
});

let _dispatch: AppDispatch | null = null;
let _getToken: (() => string | null) | null = null;

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}[] = [];

const shouldUseBlob = (url?: string) => {
  return url?.includes('/preview') || url?.includes('/download') || url?.includes('/export');
};

api.interceptors.request.use(
  (config) => {
    const isBlob = config.responseType === 'blob' || shouldUseBlob(config.url);

    config.headers.Accept = isBlob ? '*/*' : 'application/json';
    const token = _getToken?.();
    if (!config.url?.includes('refresh')) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (!(config.data instanceof FormData)) {
      config.data =
        typeof config.data === 'string'
          ? config.data
          : config.data
            ? JSON.stringify(config.data)
            : null;
      config.headers['Content-Type'] = 'application/json';
    }

    config.responseType = isBlob ? 'blob' : 'json';

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _skipAuthRefresh?: boolean;
  }
  interface AxiosRequestConfig {
    _skipAuthRefresh?: boolean;
  }
}

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    if (
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/logout') ||
      originalRequest.url?.includes('/auth/login') ||
      originalRequest._skipAuthRefresh
    ) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const token = await _dispatch?.(refreshTokenThunk()).unwrap();
      processQueue(null, token ?? null);
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return api(originalRequest);
    } catch (err) {
      processQueue(err, null);
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

export const injectDispatch = (dispatch: AppDispatch) => {
  _dispatch = dispatch;
};

export const injectGetToken = (getToken: () => string | null) => {
  _getToken = getToken;
};

export default api;
